import { useTranslation } from '@unifyapps/i18n/client';
import SvgChevronDown from '@unifyapps/icons/outline/ChevronDown';
import Plus from '@unifyapps/icons/outline/Plus';
import { Button } from '@unifyapps/ui/components/Button';
import { MenuList } from '@unifyapps/ui/components/Menu';
import type { ListMenuItem } from '@unifyapps/ui/components/Menu/MenuList';
import { Popover } from '@unifyapps/ui/components/Popover';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useAppThemeConfigContext } from '../../context/AppThemeConfigContext';
import { useCustomFontActions } from '../../context/CustomFontActionsContext';
import type { ThemeBuilderVariant } from '../../types';
import { CODE_FONT_FAMILY_TOKEN } from './config';

export const DEFAULT_FONT_FAMILY = 'var(--font-geist)';
// The JetBrains Mono font-family string is used as the default code font.
// This must stay in sync with the fallback list in packages/carbon/src/utils/fonts.ts `jetBrainsMono`.
export const DEFAULT_CODE_FONT_FAMILY =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace";

// Unified list of fonts, including both text and code fonts
export const FONT_FAMILY_OPTIONS: ListMenuItem[] = [
  {
    id: 'var(--font-dm-sans)',
    title: 'DM Sans',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-dm-sans)]',
  },
  {
    id: 'var(--font-figtree)',
    title: 'Figtree',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-figtree)]',
  },
  {
    id: 'var(--font-ibm-plex-sans-arabic)',
    title: 'IBM Plex Sans Arabic',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-ibm-plex-sans-arabic)]',
  },
  {
    id: 'var(--font-geist)',
    title: 'Geist',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-geist)]',
  },
  {
    id: 'var(--font-instrument-sans)',
    title: 'Instrument Sans',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-instrument-sans)]',
  },
  {
    id: 'var(--font-inter)',
    title: 'Inter',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-inter)]',
  },
  {
    id: 'var(--font-lato)',
    title: 'Lato',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-lato)]',
  },
  {
    id: 'var(--font-lexend)',
    title: 'Lexend',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-lexend)]',
  },
  {
    id: 'var(--font-manrope)',
    title: 'Manrope',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-manrope)]',
  },
  {
    id: 'var(--font-merriweather)',
    title: 'Merriweather',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-merriweather)]',
  },
  {
    id: DEFAULT_CODE_FONT_FAMILY,
    title: 'Mono',
    type: 'label',
    titleClassName: 'font-mono',
  },
  {
    id: 'var(--font-nunito)',
    title: 'Nunito',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-nunito)]',
  },
  {
    id: 'var(--font-open-sans)',
    title: 'Open Sans',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-open-sans)]',
  },
  {
    id: 'var(--font-poppins)',
    title: 'Poppins',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-poppins)]',
  },
  {
    id: 'var(--font-roboto)',
    title: 'Roboto',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-roboto)]',
  },
  {
    id: 'var(--font-source-serif-4)',
    title: 'Source Serif 4',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-source-serif-4)]',
  },
  {
    id: 'var(--font-spectral)',
    title: 'Spectral',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-spectral)]',
  },
  {
    id: 'var(--font-special-gothic-expanded-one)',
    title: 'Special Gothic Expanded One',
    type: 'label',
    titleClassName: 'font-[family-name:var(--font-special-gothic-expanded-one)]',
  },
];

export default function FontFamilySelector(props: {
  tokens: string[];
  disabled?: boolean;
  variant?: ThemeBuilderVariant;
}) {
  const { tokens, disabled = false, variant = 'default' } = props;
  const { t } = useTranslation(['theme', 'common']);
  const [open, setOpen] = useState(false);
  const { setFontFamilyConfig, theme } = useAppThemeConfigContext();
  const { customFontFamilies, onAddCustomFont, onManageFonts } = useCustomFontActions();
  const token = tokens[0];
  const tokenValue =
    theme?.[token] ??
    (token === CODE_FONT_FAMILY_TOKEN[0] ? DEFAULT_CODE_FONT_FAMILY : DEFAULT_FONT_FAMILY);

  // Find the selected font in the unified FONTS array
  const selectedFont = useMemo(
    () => FONT_FAMILY_OPTIONS.find((font) => font.id === tokenValue),
    [tokenValue],
  );
  // the token may point at a custom font (value = family name), absent from FONT_FAMILY_OPTIONS
  const selectedCustomFont = useMemo(
    () => customFontFamilies.find((font) => font.value === tokenValue),
    [customFontFamilies, tokenValue],
  );
  const selectedLabel = selectedFont?.title ?? selectedCustomFont?.label ?? tokenValue;

  const onAction = useCallback(
    (id: string) => {
      if (disabled) return;
      const output = tokens.reduce((acc, tkn) => {
        acc[tkn] = id;
        return acc;
      }, {});
      setFontFamilyConfig(output);
    },
    [disabled, setFontFamilyConfig, tokens],
  );

  const menuListItems = useMemo(() => {
    return FONT_FAMILY_OPTIONS.map((font) => {
      const isSelected = font.id === tokenValue;
      return {
        ...font,
        selected: isSelected,
        className: isSelected ? '!bg-brand-secondary' : undefined,
      };
    });
  }, [tokenValue]);

  const customMenuItems = useMemo<ListMenuItem[]>(
    () =>
      customFontFamilies.map((font) => ({
        id: font.value,
        title: font.label,
        type: 'label',
        selected: font.value === tokenValue,
        className: font.value === tokenValue ? '!bg-brand-secondary' : undefined,
      })),
    [customFontFamilies, tokenValue],
  );

  const handleAddCustomFont = useCallback(() => {
    setOpen(false);
    onAddCustomFont?.();
  }, [onAddCustomFont]);

  const handleManageFonts = useCallback(() => {
    setOpen(false);
    onManageFonts?.();
  }, [onManageFonts]);

  return (
    <Popover onOpenChange={disabled ? undefined : setOpen} open={disabled ? false : open}>
      <Popover.Button>
        <Button
          className={clsx('justify-between', {
            // !border-tertiary + !max-h: MUI Joy's variant styles outrank plain tailwind utilities
            // here (border-color forced transparent, max-h-8 clamps below Figma's 36px)
            '!bg-primary !border !border-tertiary w-[231px] !h-[36px] !max-h-[36px] rounded-md !shadow-none':
              variant === 'detailed',
            '!bg-utility-gray-50 h-[28px] w-full': variant === 'default',
            '[&&]:!border [&&]:!border-brand-secondary': open,
          })}
          color="neutral"
          disabled={disabled}
          endDecoratorComponent={SvgChevronDown}
          size="md"
          variant="soft"
        >
          <Typography
            className={selectedFont?.titleClassName}
            color="text-secondary"
            maxNumberOfLines={1}
            variant={variant === 'detailed' ? 'text-sm' : 'text-xs'}
            weight="regular"
          >
            {selectedLabel}
          </Typography>
        </Button>
      </Popover.Button>
      <Popover.Content placement="bottom-start">
        <Stack className="min-w-[220px]">
          {/* scrollable font list; the add-font footer below stays pinned (sibling of the scroll area) */}
          <Stack className="max-h-[280px] overflow-y-auto py-xs">
            {customMenuItems.length ? (
              <>
                <Stack
                  alignItems="center"
                  className="px-lg pb-xxs pt-xs"
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography
                    className="uppercase"
                    color="text-quaternary"
                    variant="text-xxs"
                    weight="semi-bold"
                  >
                    {t('common:Custom')}
                  </Typography>
                  {onManageFonts ? (
                    <Button color="brand" onClick={handleManageFonts} size="sm" variant="ghost">
                      {t('theme:TypographyConfig.Manage')}
                    </Button>
                  ) : null}
                </Stack>
                <MenuList items={customMenuItems} onAction={onAction} size="sm" />
                <Stack className="px-lg pb-xxs pt-md">
                  <Typography
                    className="uppercase"
                    color="text-quaternary"
                    variant="text-xxs"
                    weight="semi-bold"
                  >
                    {t('common:System')}
                  </Typography>
                </Stack>
              </>
            ) : null}
            <MenuList items={menuListItems} onAction={onAction} size="sm" />
          </Stack>
          {onAddCustomFont ? (
            // pinned add-font footer; mirrors the DS ListBoxFooter chrome (stable top
            // separator + overflow shadow). symmetric padding insets the button so its
            // hover highlight never touches the separator — no border toggling on hover
            <Stack className="border-tertiary bg-primary shadow-overflow-top border-t p-sm">
              <Button
                // ghost variant adds hover:underline; override it so only the bg highlight shows
                className="h-7 w-full justify-center rounded-md hover:!bg-secondary hover:!no-underline"
                color="brand"
                onClick={handleAddCustomFont}
                size="sm"
                startDecoratorComponent={Plus}
                variant="ghost"
              >
                {t('theme:TypographyConfig.AddCustomFont')}
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </Popover.Content>
    </Popover>
  );
}
export function getFontFamilyLabel(fontFamily: string) {
  const label = FONT_FAMILY_OPTIONS.find((font) => font.id === fontFamily)?.title ?? fontFamily;

  return label || '—';
}
